import React from 'react';
import { Alert, Box, Card, CardContent, CircularProgress, Container, Typography } from '@mui/material';

type TowerViewUnit = {
  id: number;
  projectName: string;
  unitNumber: string;
  blockNumber: string | null;
  bedroom: number;
  bathroom: number;
  areaSqft: number;
  price: number | null;
  priceNps?: number | null;
  priceDps?: number | null;
  psf: number | null;
  psfNps?: number | null;
  psfDps?: number | null;
  unitStatus: string | null;
  unitType?: string | null;
  unitTypeDescription?: string | null;
  projectUnitTypeId?: number | null;
};

type TowerViewBlockData = {
  floors: number[];
  stacks: number[];
  grid: Map<string, TowerViewUnit>;
};

type TowerViewPublicResponse = {
  project: { id: string; name: string };
  stacks: number[];
  units: TowerViewUnit[];
  showPsfInTowerView?: boolean;
  /** When false, all units use the default cell color (no sold/available colors). Null/omitted → show. */
  unitChoiceShowStatus?: boolean;
  /** When false, unit prices are hidden. Null/omitted → show. */
  unitChoiceShowPrice?: boolean;
};

const rawApiUrl = (process.env.REACT_APP_API_URL || process.env.REACT_APP_API_BASE_URL || '').replace(/\/+$/, '');
const useHostingProxy = rawApiUrl === '' || rawApiUrl === '/';
const functionBase = useHostingProxy ? '' : rawApiUrl.endsWith('/api') ? rawApiUrl : `${rawApiUrl}/api`;
const apiBase = useHostingProxy ? '/api' : `${functionBase}/api`;

const parseUnitNumber = (unitNumber: string): { floor: number | null; stack: number | null } => {
  const match = unitNumber.match(/#?\s*(B?)(\d+)-(\d+)/i);
  if (match) {
    const isBasement = match[1].toUpperCase() === 'B';
    const floorNumber = Number(match[2]);
    const stackNumber = Number(match[3]);
    return {
      floor: Number.isFinite(floorNumber) ? (isBasement ? -floorNumber : floorNumber) : null,
      stack: Number.isFinite(stackNumber) ? stackNumber : null,
    };
  }

  const digits = unitNumber.replace(/\D/g, '');
  if (digits.length < 2) return { floor: null, stack: null };
  const stack = Number(digits.slice(-2));
  const floor = digits.length > 2 ? Number(digits.slice(0, -2)) : null;
  return {
    floor: Number.isFinite(floor) ? floor : null,
    stack: Number.isFinite(stack) ? stack : null,
  };
};

const formatCurrency = (value: number | null | undefined): string => {
  const numberValue = Number(value);
  if (!Number.isFinite(numberValue) || numberValue <= 0) return '-';
  if (numberValue >= 1000000) return `$${(numberValue / 1000000).toFixed(2)}M`;
  if (numberValue >= 1000) return `$${Math.round(numberValue / 1000)}K`;
  return `$${Math.round(numberValue).toLocaleString()}`;
};

const formatBlockNumber = (blockNumber: string | null): string => {
  if (!blockNumber) return 'Block';
  return blockNumber.toLowerCase().startsWith('block') ? blockNumber : `Block ${blockNumber}`;
};

const buildTowerViewData = (units: TowerViewUnit[], requestedStacks: number[]): { blocks: Map<string, TowerViewBlockData>; blockNumbers: string[] } => {
  const blocks = new Map<string, TowerViewBlockData>();
  const alignedStacks = Array.from(new Set(requestedStacks)).sort((a, b) => a - b);

  units.forEach((unit) => {
    const parsed = parseUnitNumber(unit.unitNumber);
    if (parsed.floor == null || parsed.stack == null) return;
    if (!alignedStacks.includes(parsed.stack)) return;

    const block = unit.blockNumber || 'Block';
    const current = blocks.get(block) || { floors: [], stacks: [], grid: new Map<string, TowerViewUnit>() };

    if (!current.floors.includes(parsed.floor)) current.floors.push(parsed.floor);
    if (!current.stacks.includes(parsed.stack)) current.stacks.push(parsed.stack);
    current.grid.set(`${parsed.floor}-${parsed.stack}`, unit);
    blocks.set(block, current);
  });

  blocks.forEach((block) => {
    block.floors.sort((a, b) => b - a);
    block.stacks.sort((a, b) => a - b);
  });

  return {
    blocks,
    blockNumbers: Array.from(blocks.keys()).sort((a, b) => a.localeCompare(b, undefined, { numeric: true })),
  };
};

const TowerViewPublicApp: React.FC = () => {
  const [data, setData] = React.useState<TowerViewPublicResponse | null>(null);
  const [loading, setLoading] = React.useState(true);
  const [error, setError] = React.useState<string | null>(null);
  const [info, setInfo] = React.useState<string | null>(null);

  React.useEffect(() => {
    const params = new URLSearchParams(window.location.search);
    const projectId = params.get('projectId');
    const stack = params.get('stack');
    const secret = params.get('secret');
    const stackValue = stack?.trim() ?? '';

    if (projectId && !stackValue) {
      setInfo('Please input unit choices on the left to view stacks');
      setLoading(false);
      return;
    }

    if (!projectId || !stackValue || !secret || !/^\d+(,\d+)*$/.test(stackValue)) {
      setError('This public tower view link is invalid.');
      setLoading(false);
      return;
    }

    const requestParams = new URLSearchParams({ projectId, stack: stackValue, secret });
    fetch(`${apiBase}/public/tower-view?${requestParams.toString()}`)
      .then(async (response) => {
        const body = await response.json().catch(() => ({}));
        if (!response.ok) throw new Error(body.error || 'Unable to load tower view');
        return body as TowerViewPublicResponse;
      })
      .then((responseData) => {
        const availableStacks = new Set(
          responseData.units
            .map((unit) => parseUnitNumber(unit.unitNumber).stack)
            .filter((value): value is number => value != null)
        );
        const missingStacks = responseData.stacks.filter((requestedStack) => !availableStacks.has(requestedStack));
        if (missingStacks.length > 0) {
          throw new Error(`Requested stack${missingStacks.length > 1 ? 's' : ''} ${missingStacks.map((stackNumber) => String(stackNumber).padStart(2, '0')).join(', ')} not found for this project.`);
        }
        setData(responseData);
      })
      .catch((fetchError) => setError(fetchError instanceof Error ? fetchError.message : 'Unable to load tower view'))
      .finally(() => setLoading(false));
  }, []);

  const towerViewData = React.useMemo(() => buildTowerViewData(data?.units || [], data?.stacks || []), [data]);
  const showStatus = data?.unitChoiceShowStatus !== false;
  const showPrice = data?.unitChoiceShowPrice !== false;

  if (loading) {
    return (
      <Container maxWidth="lg" sx={{ py: 8, textAlign: 'center' }}>
        <CircularProgress />
      </Container>
    );
  }

  if (info) {
    return (
      <Container maxWidth={false} sx={{ py: 8, display: 'flex', justifyContent: 'flex-start' }}>
        <Alert severity="info" sx={{ mb: 2 }}>{info}</Alert>
      </Container>
    );
  }

  if (error || !data) {
    return (
      <Container maxWidth={false} sx={{ py: 8, display: 'flex', justifyContent: 'flex-start' }}>
        <Alert severity="error" sx={{ mb: 2 }}>{error || 'Invalid public tower view link.'}</Alert>
      </Container>
    );
  }

  return (
    <Container maxWidth={false} sx={{ py: 3, color: 'var(--color-text)' }}>
      <Box sx={{ mb: 2 }}>
        <Typography variant="h5" sx={{ fontWeight: 700 }}>{data.project.name}</Typography>
        <Typography variant="body2" color="text.secondary">Stacks {data.stacks.map((stack) => String(stack).padStart(2, '0')).join(', ')}</Typography>
      </Box>

      <Card sx={{ bgcolor: 'var(--color-card)', color: 'var(--color-text)', border: '1px solid rgba(var(--color-text-rgb), 0.12)' }}>
        <CardContent>
          <Box sx={{ overflow: 'auto', pb: 1 }}>
            <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 2, width: 'fit-content', minWidth: '100%' }}>
              {towerViewData.blockNumbers.map((block) => {
                const blockData = towerViewData.blocks.get(block);
                if (!blockData) return null;
                const cellWidth = 104;

                return (
                  <Box key={block} sx={{ flex: '0 0 auto' }}>
                    <Box sx={{ p: 1, textAlign: 'center', fontWeight: 700, borderRadius: '6px 6px 0 0', bgcolor: 'var(--color-accent)', color: 'var(--color-text-inverse)' }}>
                      {formatBlockNumber(block)}
                    </Box>
                    <Box sx={{ display: 'grid', gridTemplateColumns: `40px repeat(${blockData.stacks.length}, ${cellWidth}px)`, gap: '1px', width: 'fit-content', p: '1px', bgcolor: 'rgba(var(--color-text-rgb), 0.16)', borderRadius: '0 0 6px 6px' }}>
                      <Box sx={{ bgcolor: 'rgba(var(--color-shadow-rgb), 0.35)' }} />
                      {blockData.stacks.map((stack) => {
                        const stackUnits = blockData.floors.map((floor) => blockData.grid.get(`${floor}-${stack}`)).filter((unit): unit is TowerViewUnit => Boolean(unit));
                        const firstUnit = stackUnits[0];
                        const description = firstUnit?.unitTypeDescription?.replace(/Bedroom/gi, 'BR') || '';
                        const size = firstUnit?.areaSqft ? `${firstUnit.areaSqft} sqft` : '';
                        const code = firstUnit?.unitType || '';

                        return (
                          <Box key={stack} sx={{ minHeight: 48, textAlign: 'center', fontSize: '0.72rem', fontWeight: 700, py: 0.5, px: 0.25, display: 'flex', flexDirection: 'column', justifyContent: 'center', alignItems: 'center', bgcolor: 'rgba(var(--color-shadow-rgb), 0.35)', color: 'var(--color-text-strong)' }}>
                            <Typography component="span" sx={{ fontSize: '0.72rem', fontWeight: 700, lineHeight: 1.2 }}>{String(stack).padStart(2, '0')}</Typography>
                            {description ? <Typography component="span" sx={{ fontSize: '0.6rem', fontWeight: 600, lineHeight: 1.2 }}>{description}</Typography> : null}
                            <Typography component="span" sx={{ fontSize: '0.58rem', lineHeight: 1.2 }}>{[code, size].filter(Boolean).join(' / ') || '-'}</Typography>
                          </Box>
                        );
                      })}
                      {blockData.floors.map((floor) => (
                        <React.Fragment key={`${block}-${floor}`}>
                          <Box sx={{ textAlign: 'center', fontSize: '0.72rem', fontWeight: 700, display: 'flex', alignItems: 'center', justifyContent: 'center', py: 0.25, bgcolor: 'rgba(var(--color-shadow-rgb), 0.35)', color: 'var(--color-text-strong)' }}>{floor}</Box>
                          {blockData.stacks.map((stack) => {
                            const unit = blockData.grid.get(`${floor}-${stack}`);
                            const isSold = (unit?.unitStatus || '').toLowerCase() === 'sold';
                            const npsPrice = unit?.priceNps ?? unit?.price ?? null;
                            const useStatusColors = Boolean(unit) && showStatus;
                            const statusLabel =
                              unit && showStatus && isSold
                                ? 'SOLD'
                                : unit && showPrice
                                  ? formatCurrency(npsPrice)
                                  : null;

                            return (
                              <Box
                                key={`${block}-${floor}-${stack}`}
                                sx={{
                                  width: cellWidth,
                                  minHeight: 74,
                                  px: 0.75,
                                  py: 0.6,
                                  bgcolor: !unit
                                    ? 'rgba(var(--color-shadow-rgb), 0.18)'
                                    : useStatusColors
                                      ? isSold
                                        ? 'var(--color-danger)'
                                        : 'color-mix(in srgb, var(--color-success) 55%, #000000)'
                                      : 'rgba(var(--color-shadow-rgb), 0.35)',
                                  color: !unit
                                    ? 'rgba(var(--color-text-rgb), 0.9)'
                                    : useStatusColors
                                      ? 'var(--color-text-inverse)'
                                      : 'var(--color-text-strong)',
                                  border: '1px solid',
                                  borderColor: !unit
                                    ? 'rgba(var(--color-text-rgb), 0.45)'
                                    : useStatusColors
                                      ? 'rgba(255,255,255,0.65)'
                                      : 'rgba(var(--color-text-rgb), 0.35)',
                                  borderRadius: 0.5,
                                  boxSizing: 'border-box',
                                  display: 'flex',
                                  flexDirection: 'column',
                                  alignItems: 'center',
                                  justifyContent: 'flex-start',
                                  lineHeight: 1.05,
                                  gap: 0.15,
                                }}
                              >
                                <Typography sx={{ fontSize: unit ? '0.66rem' : '0.9rem', fontWeight: 700, whiteSpace: 'nowrap' }}>{unit?.unitNumber || '-'}</Typography>
                                {unit ? <Typography sx={{ fontSize: '0.58rem', whiteSpace: 'nowrap' }}>{unit.bedroom ? `${unit.bedroom}BR` : '-'}</Typography> : null}
                                {statusLabel != null ? (
                                  <Typography sx={{ fontSize: '0.56rem', whiteSpace: 'nowrap' }}>{statusLabel}</Typography>
                                ) : null}
                                {unit ? <Typography sx={{ fontSize: '0.56rem', whiteSpace: 'nowrap' }}>{unit.areaSqft ? `${unit.areaSqft} sqft` : '-'}</Typography> : null}
                              </Box>
                            );
                          })}
                        </React.Fragment>
                      ))}
                    </Box>
                  </Box>
                );
              })}
            </Box>
          </Box>
        </CardContent>
      </Card>
    </Container>
  );
};

export default TowerViewPublicApp;
